import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CookiesStorageService } from '@/services/cookiesStorageService';
import { LocalStorageService } from '@/services/localStorageService';
import { generateUrlParams } from '@/helpers/generateUrlParams';
import { menuData } from '@/data/menuData';
import { COOKIES_KEYS, LOCAL_STORAGE_KEYS } from '@/constants';
import { GlobalContext } from './GlobalContext';
import { globalInitialState } from './globalInitialState';

interface GlobalContextProviderProps {
	children: React.ReactNode;
}
const DynamicMainLoader = dynamic(
	async () =>
		await import('@/components/elements/MainLoader').then(
			mod => mod.MainLoader,
		),
	{ ssr: false },
);

export const GlobalProvider: React.FC<GlobalContextProviderProps> = ({
	children,
}) => {
	// States
	const [isLoading, setIsLoading] = React.useState(
		globalInitialState.isLoading,
	);
	const [uuid, setUuid] = React.useState(() => {
		return CookiesStorageService.get(
			COOKIES_KEYS.UUID,
			globalInitialState.uuid,
		);
	});

	// Helpers
	const router = useRouter();
	const memoizedValue = React.useMemo(
		() => ({
			uuid,
			isLoading,
		}),
		[uuid, isLoading],
	);
	const isOnboarding = router.pathname.includes('/onboarding/[[...pages]]');

	// Effects
	React.useEffect(() => {
		// set menu data to local storage
		LocalStorageService.set(LOCAL_STORAGE_KEYS.MENU_DATA, menuData);
	}, []);

	// get uuid from url or cookies or generate new one
	React.useEffect(() => {
		if (uuid) {
			setIsLoading(false);
		} else {
			const { uuid: uuidFromUrl } = generateUrlParams<Record<string, string>>();
			const correctUuid = uuidFromUrl ?? uuidv4();

			// set uuid in cookies and in state
			const timestamp = new Date().getTime();
			const expire = timestamp + 60 * 60 * 24 * 1000 * 365;
			CookiesStorageService.set(COOKIES_KEYS.UUID, correctUuid, {
				path: '/',
				maxAge: expire,
			});

			setUuid(correctUuid);
		}
	}, [uuid]);

	return (
		<GlobalContext.Provider value={memoizedValue}>
			{children}

			<DynamicMainLoader isLoading={isLoading} isOnboarding={isOnboarding} />
		</GlobalContext.Provider>
	);
};
